<template>
  <form class="conversation--form" @submit.prevent="handleConversations">
    <div class="form-background">
      <img
        :src="require('../../../../assets/logoInterativa-gray')"
        alt="Logo Interativa"
      />
    </div>
    <div v-if="showNoInboxAlert" class="callout warning">
      <p>
        {{ $t('NEW_CONVERSATION.NO_INBOX') }}
      </p>
    </div>
    <div v-else>
      <div class="row gutter-small">
        <div class="columns">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
          </label>
          <div class="multiselect-wrap--small">
            <multiselect
              v-model="targetInbox"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label=""
              select-label=""
              deselect-label=""
              :max-height="160"
              :close-on-select="true"
              :options="[...inboxes]"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <inbox-dropdown-item
                  v-if="option.name"
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
                <span v-else>
                  {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
                </span>
              </template>
              <template slot="option" slot-scope="{ option }">
                <inbox-dropdown-item
                  :name="option.name"
                  :inbox-identifier="computedInboxSource(option)"
                  :channel-type="option.channel_type"
                />
              </template>
            </multiselect>
          </div>
          <label :class="{ error: $v.targetInbox.$error }">
            <span v-if="$v.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>
        <div class="columns">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.TO.LABEL') }}
            <div class="contact-input">
              <thumbnail
                :src="contact.thumbnail"
                size="24px"
                :username="contact.name"
                :status="contact.availability_status"
              />
              <h4 class="text-block-title contact-name">
                {{ contact.name }}
              </h4>
            </div>
          </label>
        </div>
      </div>
      <div v-if="isAnEmailInbox" class="row">
        <div class="columns">
          <label :class="{ error: $v.subject.$error }">
            {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}
            <input
              v-model="subject"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
              @input="$v.subject.$touch"
            />
            <span v-if="$v.subject.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
            </span>
          </label>
        </div>
      </div>
      <div class="row">
        <div class="columns">
          <div class="canned-response">
            <canned-response
              v-if="showCannedResponseMenu && hasSlashCommand"
              :search-key="cannedResponseSearchKey"
              @click="replaceTextWithCannedResponse"
            />
          </div>
          <div v-if="isEmailOrWebWidgetInbox">
            <label>
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
              <reply-email-head
                v-if="isAnEmailInbox"
                :cc-emails.sync="ccEmails"
                :bcc-emails.sync="bccEmails"
              />
              <label class="editor-wrap">
                <woot-message-editor
                  v-model="message"
                  class="message-editor"
                  :class="{ editor_warning: $v.message.$error }"
                  :enable-variables="true"
                  :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                  @toggle-canned-menu="toggleCannedMenu"
                  @blur="$v.message.$touch"
                />
                <span v-if="$v.message.$error" class="editor-warning__message">
                  {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
                </span>
              </label>
            </label>
          </div>
          <whatsapp-templates
            v-else-if="hasWhatsappTemplates"
            :contact="contact"
            :inbox-id="selectedInbox.inbox.id"
            @on-select-template="toggleWaTemplate"
            @on-send="onSendWhatsAppReply"
          />
          <label
            v-if="
              !hasWhatsappTemplates &&
                hasWhatsappTemplates !== null &&
                !isEmailOrWebWidgetInbox
            "
            :class="{ error: $v.message.$error }"
          >
            {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            <textarea
              v-if="!isEmailOrWebWidgetInbox"
              v-model="message"
              class="message-input"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
              @input="$v.message.$touch"
            />
            <span
              v-if="$v.message.$error && !isEmailOrWebWidgetInbox"
              class="message"
            >
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
            </span>
          </label>
        </div>
      </div>
    </div>
    <div v-if="!hasWhatsappTemplates" class="modal-footer">
      <button class="button clear" @click.prevent="onCancel">
        {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
      </button>
      <woot-button
        class="bg-primary"
        :class="{
          'button-disabled': isSubmitting || conversationsUiFlags.isCreating,
        }"
        type="submit"
        :is-loading="conversationsUiFlags.isCreating"
        :disabled="isSubmitting || conversationsUiFlags.isCreating"
      >
        {{ $t('NEW_CONVERSATION.FORM.SUBMIT') }}
      </woot-button>
    </div>
  </form>
</template>

<script>
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import { getInboxSource } from 'dashboard/helper/inbox';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import alertMixin from 'shared/mixins/alertMixin';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import WhatsappTemplates from './WhatsappTemplates.vue';
export default {
  components: {
    Thumbnail,
    WootMessageEditor,
    ReplyEmailHead,
    CannedResponse,
    WhatsappTemplates,
    InboxDropdownItem,
  },
  mixins: [alertMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    isSurvey: {
      type: Boolean,
      default: false,
    },
    survey: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      name: '',
      subject: '',
      message: '',
      showCannedResponseMenu: false,
      cannedResponseSearchKey: '',
      bccEmails: '',
      ccEmails: '',
      targetInbox: {},
      conversations: [],
      whatsappTemplateSelected: false,
      hasWhatsappTemplates: null,
      isSubmitting: false,
    };
  },
  validations: {
    subject: {
      required: requiredIf('isAnEmailInbox'),
    },
    message: {
      required,
    },
    targetInbox: {
      required,
    },
  },

  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
    }),
    openConversations() {
      return this.conversations.filter(
        conversation =>
          conversation.inbox_id == this.targetInbox.id &&
          conversation?.meta?.channel !== 'Channel::Internal' &&
          conversation.status !== 'resolved'
      );
    },
    emailMessagePayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: { content: this.message },
        mailSubject: this.subject,
        assigneeId: this.currentUser.id,
      };
      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }

      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }
      return payload;
    },
    contactableInboxList() {
      const contactState = this.$store.getters['contacts/getContact'](
        this.contact.id
      );
      return (
        contactState?.contactableInboxes ||
        this.contact?.contactableInboxes ||
        []
      );
    },
    selectedInbox: {
      get() {
        const inboxList = this.contactableInboxList;
        return (
          inboxList.find(inbox => inbox.inbox.id === this.targetInbox.id) || {
            inbox: {},
          }
        );
      },
      set(value) {
        this.targetInbox = value.inbox;
      },
    },
    showNoInboxAlert() {
      if (!this.contactableInboxList.length && !this.contact?.id) {
        return false;
      }
      return this.inboxes.length === 0 && !this.uiFlags.isFetchingInboxes;
    },
    inboxes() {
      const inboxList = this.contactableInboxList;

      const validatedInboxes = inboxList
        .filter(inbox => {
          if (this.isSurvey) {
            return (
              inbox.inbox.channel_type === 'Channel::Api' ||
              inbox.inbox.channel_type === 'Channel::Whatsapp' ||
              inbox.inbox.channel_type === 'Channel::Email'
            );
          }

          return true;
        })
        .map(inbox => ({
          ...inbox.inbox,
          sourceId: inbox.source_id,
        }));
      return validatedInboxes;
    },
    isAnEmailInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.EMAIL
      );
    },
    isAnWebWidgetInbox() {
      return (
        this.selectedInbox &&
        this.selectedInbox.inbox.channel_type === INBOX_TYPES.WEB
      );
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailInbox || this.isAnWebWidgetInbox;
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    selectedOpenConversation() {
      return this.openConversations[0] || null;
    },
  },
  watch: {
    message(value) {
      this.hasSlashCommand = value[0] === '/' && !this.isEmailOrWebWidgetInbox;
      const hasNextWord = value.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.cannedResponseSearchKey = value.substring(1);
        this.showCannedResponseMenu = true;
      } else {
        this.cannedResponseSearchKey = '';
        this.showCannedResponseMenu = false;
      }
    },

    async targetInbox(inbox) {
      this.hasWhatsappTemplates = await this.needTemplate(inbox);
    },
    'contact.id'() {
      this.isSubmitting = false;
    },
  },
  mounted() {
    this.$store.dispatch('contacts/fetchContactableInbox', this.contact?.id);
    if (this.activeInbox) {
      this.targetInbox = this.$store.getters['inboxes/getInbox'](
        this.activeInbox
      );
    }
    this.isSubmitting = true;
    this.contactConversations(this.contact).finally(() => {
      this.isSubmitting = false;
    });
    if (this.isSurvey) {
      this.message = this.survey.message;
    }
  },

  methods: {
    async contactConversations(contact) {
      await this.$store.dispatch('contactConversations/fetch', contact.id);
      const responseConversations = await this.$store.getters[
        'contactConversations/getConversations'
      ];

      const itensSelecionados = [];

      Object.keys(responseConversations).forEach(key => {
        if (+key === +contact.id) {
          itensSelecionados.push(...responseConversations[key]);
        }
      });

      this.conversations = itensSelecionados;
      return itensSelecionados;
    },
    async needTemplate(inbox) {
      if (inbox?.id && inbox?.channel_type === 'Channel::Whatsapp') {
        const params = {
          contactId: this.contact?.id,
          inboxId: inbox?.id,
          accountId: this.accountId,
        };
        const needTemplate = await this.$store.dispatch(
          'contactConversations/needTemplate',
          params
        );
        const result =
          !!this.selectedInbox.inbox?.message_templates && needTemplate.result;
        return result;
      }
      return false;
    },
    onCancel() {
      this.isSubmitting = false;
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    replaceTextWithCannedResponse(message) {
      this.message = message;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    prepareWhatsAppMessagePayload({ message: content, templateParams, url }) {
      const type = templateParams?.components?.find(
        item =>
          item.format === 'IMAGE' ||
          item.format === 'DOCUMENT' ||
          item.format === 'VIDEO'
      );

      let sendType = '';
      if (type) {
        sendType =
          type?.format?.toLowerCase() === 'document'
            ? 'file'
            : type.format.toLowerCase();
      }

      const payload = {
        inboxId: this.targetInbox.id,
        sourceId: this.targetInbox.sourceId,
        contactId: this.contact.id,
        message: {
          content,
          template_params: { ...templateParams },
          url,
          type: sendType,
        },
        assigneeId: this.currentUser.id,
      };

      return payload;
    },
    async sendMessage(messagePayload = {}) {
      const conversationId = this.selectedOpenConversation?.id;
      if (!conversationId) {
        this.isSubmitting = false;
        return;
      }

      const payload = {
        conversationId,
        message: this.message,
        templateParams: {},
        private: false,
        sender: this.sender,
        reply_id: null,
        ...messagePayload,
      };
      try {
        await this.$store.dispatch('createPendingMessageAndSend', payload);
        const action = {
          type: 'link',
          to: `/app/accounts/${this.accountId}/conversations/${conversationId}`,
          message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        };
        this.isSubmitting = false;
        this.showAlert(
          this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'),
          action
        );
        this.onSuccess();
      } catch (error) {
        this.isSubmitting = false;
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    handleConversations() {
      if (this.isSubmitting || this.hasWhatsappTemplates) {
        return;
      }
      this.isSubmitting = true;
      if (this.openConversations.length > 0) {
        this.isSubmitting = false;
        this.showAlert(
          this.$t('NEW_CONVERSATION.ALERT.ONGOING_CONVERSATION_NOTICE')
        );
        return;
      }
      this.onFormSubmit();
    },
    onFormSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.isSubmitting = false;
        return;
      }
      this.createConversation(this.emailMessagePayload);
    },
    async createConversation(payload) {
      try {
        const data = await this.onSubmit(payload);
        const action = {
          type: 'link',
          to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
          message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
        };
        this.isSubmitting = false;
        this.onSuccess();
        this.showAlert(
          this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'),
          action
        );
      } catch (error) {
        this.isSubmitting = false;
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },

    toggleWaTemplate(val) {
      this.whatsappTemplateSelected = val;
    },
    async onSendWhatsAppReply(messagePayload) {
      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;
      const payload = this.prepareWhatsAppMessagePayload(messagePayload);
      if (this.openConversations?.length) {
        this.isSubmitting = false;
        this.showAlert(
          this.$t('NEW_CONVERSATION.ALERT.ONGOING_CONVERSATION_NOTICE')
        );
      } else {
        await this.createConversation(payload);
      }
    },
    inboxReadableIdentifier(inbox) {
      return `${inbox.name} (${inbox.channel_type})`;
    },
    computedInboxSource(inbox) {
      if (!inbox.channel_type) return '';
      const classByType = getInboxSource(
        inbox.channel_type,
        inbox.phone_number,
        inbox
      );
      return classByType;
    },
  },
};
</script>

<style scoped lang="scss">
.conversation--form {
  padding: var(--space-normal) var(--space-large) var(--space-large);
  min-height: 350px !important;
}

.form-background {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: -1;
  opacity: 0.1;
  margin-bottom: 80px;
}

.form-background img {
  max-width: 60%;
  max-height: 60%;
  opacity: 0.2;
}

.clear {
  color: var(--g-20) !important;
}

.canned-response {
  position: relative;
}

.input-group-label {
  font-size: var(--font-size-small);
}

.contact-input {
  display: flex;
  align-items: center;
  height: 3.9rem;
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  padding: var(--space-smaller) var(--space-small);
  border-radius: var(--border-radius-small);

  .contact-name {
    margin: 0;
    margin-left: var(--space-small);
    margin-right: var(--space-small);
  }
}

.message-input {
  min-height: 8rem;
}

.row.gutter-small {
  gap: var(--space-small);
}

::v-deep {
  .mention--box {
    left: 0;
    margin: auto;
    right: 0;
    top: unset;
    height: fit-content;
  }

  /* TODO: Remove when have standardized a component out of multiselect  */
  .multiselect .multiselect__content .multiselect__option span {
    display: inline-flex;
    width: var(--space-medium);
    color: var(--s-600);
  }
  .multiselect .multiselect__content .multiselect__option {
    padding: var(--space-micro) var(--space-smaller);
  }

  .button-disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
  }
}
</style>
